import { formatDuration } from '@liftx/core';
import { useRestTimer, type RestTimerDisplay } from './useRestTimer';
import type { ActiveRestTimer } from '../../data/types';
import styles from './Workout.module.css';

const ENDING_SOON_THRESHOLD_S = 10;

/**
 * The text announced to screen readers, kept deliberately coarse.
 *
 * `PHASE-0-ARCHITECTURE.md` §16 requires rest-completion announcements
 * "throttled so the timer does not announce every second" — the visible
 * countdown (`restTime` below) updates every frame, but a live region only
 * actually speaks when its text content changes, so returning one of exactly
 * three fixed strings (never the live second-by-second number) is what makes
 * this throttle real rather than nominal: React leaves the DOM node alone,
 * and therefore silent, for the ~50 seconds a typical rest spends above the
 * 10-second warning.
 */
export function announcementFor(display: RestTimerDisplay): string {
  if (display.isComplete) return 'Rest complete.';
  if (display.remainingSeconds <= ENDING_SOON_THRESHOLD_S) return '10 seconds left.';
  return '';
}

/**
 * The running rest timer, always visible without scrolling while active
 * (`PHASE-0-ARCHITECTURE.md` §14). Purely a display over `useRestTimer` —
 * every number here is re-derived from the persisted `{startedAt, endsAt}`,
 * never counted down locally.
 */
export function RestTimerBar({
  timer,
  onAdjust,
  onSkip,
}: {
  timer: ActiveRestTimer;
  onAdjust: (deltaS: number) => void;
  onSkip: () => void;
}): React.JSX.Element {
  const display = useRestTimer(timer);

  return (
    <div className={styles.restBar}>
      {/* Visible, updates every frame — sighted users see a live countdown. */}
      <span className={styles.restTime}>
        {display.isComplete
          ? `Rest ended ${formatDuration(display.overdueSeconds)} ago`
          : `Rest: ${formatDuration(display.remainingSeconds)}`}
      </span>
      {/* Announced, updates only at the two meaningful checkpoints above. */}
      <span className={styles.visuallyHidden} role="status" aria-live="polite">
        {announcementFor(display)}
      </span>
      <div className={styles.restControls}>
        <button type="button" className={styles.restButton} onClick={() => onAdjust(30)}>
          +30s
        </button>
        <button type="button" className={styles.restButton} onClick={onSkip}>
          Skip
        </button>
      </div>
    </div>
  );
}
